// apps/merchant-v3/src/lib/i18n/index.ts
//
// Phase 6 Wave 2 Task 06 — Public i18n entry point. Initialises i18next
// on first import and re-exports `i18n`, `useTranslation`, plus the
// canonical locale list used by the TopBar selector.
//
// Callers should import from `@/lib/i18n` (the legacy stub at
// `lib/i18n.ts` re-exports from this module so existing import paths
// from Tasks 02/03 keep working).
//
// vg-binding: PLAN:task-06
// vg-binding: P6.D-131
// vg-binding: P6.D-221

import { useTranslation, Trans, I18nextProvider } from 'react-i18next';

import { i18next, initI18n, LOCALES, NAMESPACES, userLangToLocale, localeToUserLang } from './config';
import type { Locale } from './config';
import type { SupportedLanguage } from '@/types/user';

// Boot once at module load — safe because config.ts guards re-entry.
const i18n = initI18n();

/** Mirrors the public API from the legacy stub at `lib/i18n.ts`. */
export const SUPPORTED_LANGUAGES: ReadonlyArray<{
  code: SupportedLanguage;
  label: string;
  flag: string;
}> = LOCALES.map((l) => ({ code: l.user, label: l.label, flag: l.flag }));

/**
 * Synchronous translate — safe to call outside React (e.g. error toasts
 * fired from Zustand selectors). Inside components prefer `useTranslation()`
 * which subscribes to language changes.
 */
export function t(key: string, options?: Record<string, unknown>): string {
  return i18n.t(key, options) as string;
}

/** Switch active language; persists via the cookie + localStorage detector. */
export function changeLanguage(lang: SupportedLanguage): Promise<unknown> {
  return i18n.changeLanguage(userLangToLocale(lang));
}

export {
  i18n,
  i18next,
  useTranslation,
  Trans,
  I18nextProvider,
  LOCALES,
  NAMESPACES,
  userLangToLocale,
  localeToUserLang,
};
export type { Locale };
